import { COLORS } from "@/features/shared/constants";
import TimeRange from "@/types/time-range";
import { Serie } from "@/types/serie";

import CHART_COLORS from "./time-series.constants";

export const getUniqueUnits = (series: Serie[]) => {
  const units = series.map((serie) => serie.units);

  return [...new Set(units)];
};

export const generateYAxis = (series: Serie[]) => {
  const uniqueUnits = getUniqueUnits(series);
  const yAxis: Highcharts.YAxisOptions[] = uniqueUnits.map((unit, index) => {
    const isDegreeUnit = unit === "°";

    const tickInterval = isDegreeUnit ? 45 : undefined;
    const max = isDegreeUnit ? 360 : undefined;
    const min = isDegreeUnit ? 0 : undefined;
    const opposite = index % 2 !== 0;

    return {
      title: { text: unit },
      tickInterval,
      max,
      min,
      opposite,
    };
  });

  return yAxis;
};

export const getSuffix = (series: Serie[]) => {
  const uniqueUnits = getUniqueUnits(series);

  if (uniqueUnits.length === 1) {
    return uniqueUnits[0];
  }

  return undefined;
};

export const getSerieLineStyle = (serie: Serie, series: Serie[]) => {
  const { translatedName, name } = serie;

  const indexOfSerie = series.indexOf(serie);
  const seriesName = translatedName || name;
  const hasMaxValues = seriesName.includes("max");
  const hasObservationValues = seriesName.toLowerCase().includes("obser");
  const colorIndex: keyof typeof COLORS = hasMaxValues
    ? "max"
    : indexOfSerie % 2 === 0
      ? "default0"
      : "default1";
  const palette = COLORS[colorIndex] ?? COLORS.default;
  const colorShade: keyof typeof palette = hasObservationValues ? "darker" : "normal";

  const pointFormat =
    '<span style="color:{point.color}; font-weight: bold">⦁</span> {point.y}<br/>';

  return {
    name: seriesName,
    color: palette[colorShade],
    dashStyle: hasMaxValues ? "ShortDash" : "Solid",
    lineWidth: hasObservationValues ? 3 : 1.5,
    tooltip: {
      pointFormat,
    },
  };
};

export const transformSerieData = (serie: Serie): [Date, number][] => {
  return Object.entries(serie.data).map(([dateStr, value]) => {
    return [new Date(dateStr), value];
  });
};

export const filterSerieDataByTimeRange = (
  serie: [Date, number][],
  timeRange: TimeRange,
): [Date, number][] => {
  return serie.filter(([dateStr]) => {
    const date = new Date(dateStr);
    return date >= timeRange.timeStart && date <= timeRange.timeEnd;
  });
};

export const getChartXAxis = () => {
  const xAxis: Highcharts.XAxisOptions = {
    type: "datetime",
    maxPadding: 0.05,
    alternateGridColor: CHART_COLORS.alternateGridColor,
    crosshair: true,
    plotLines: [
      {
        color: CHART_COLORS.plotLine.color,
        width: 1,
        value: new Date().getTime(),
      },
    ],
  };

  return xAxis;
};

export const getChartTooltip = (series: Serie[]) => {
  return {
    followPointer: true,
    followTouchMove: true,
    borderWidth: 2,
    shadow: true,
    backgroundColor: CHART_COLORS.tooltip.backgroundColor,
    split: true,
    valueSuffix: getSuffix(series),
  };
};

export const generateChartSeries = (series: Serie[], timeRange: TimeRange) => {
  return series.map((serie) => {
    const transformedData = transformSerieData(serie);
    const uniqueUnits = getUniqueUnits(series);
    const filteredData = filterSerieDataByTimeRange(transformedData, timeRange);
    const indexOfUnit = uniqueUnits.indexOf(serie.units);
    const lineData = filteredData.map(([date, value]) => {
      return [date.getTime(), value];
    });

    return {
      ...getSerieLineStyle(serie, series),
      data: lineData,
      yAxis: indexOfUnit % 2,
      marker: {
        enabled: false,
      },
    } as Highcharts.SeriesOptionsType;
  });
};
